import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../redux/store';
import { Link } from 'react-router-dom';
import { EnvironmentToggle } from '../common/EnvironmentToggle';

export const ApiOverview: React.FC = () => {
  const { apiBaseUrl, environment, currentApiKey } = useSelector(
    (state: RootState) => state.auth
  );

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const endpoints = [
    { method: 'POST', endpoint: '/register', description: 'Register a new restaurant', path: '/api-reference/menubuilder/register' },
    { method: 'GET', endpoint: '/restaurants', description: 'Get all restaurants', path: '/api-reference/restaurants/getrestaurants' },
    { method: 'POST', endpoint: '/merchant/config', description: 'Configure merchant settings', path: '/api-reference/merchant/merchantconfig' },
    { method: 'PUT', endpoint: '/webhook/settings', description: 'Configure order webhooks', path: '/api-reference/orders/configurewebhook' },
    { method: 'POST', endpoint: '/menu/upsert', description: 'Add or update menu items', path: '/api-reference/menuitem/upsertmenu' },
    { method: 'POST', endpoint: '/upload', description: 'Upload menu files', path: '/api-reference/menubuilder/upload' },
    { method: 'GET', endpoint: '/menu', description: 'Get menu items', path: '/api-reference/menubuilder/getmenu' },
    { method: 'GET', endpoint: '/status', description: 'Get upload status', path: '/api-reference/menubuilder/getstatus' },
    { method: 'POST', endpoint: '/order/create', description: 'Create a new order', path: '/api-reference/orders/createorder' },
    { method: 'PUT', endpoint: '/order/status', description: 'Update order status', path: '/api-reference/orders/updateorder' },
    { method: 'POST', endpoint: '/payment/status', description: 'Update payment status', path: '/api-reference/orders/paymentstatus' },
    { method: 'GET', endpoint: '/voice/number/assign', description: 'Assign Number to Order', path: '/api-reference/voice/numberassign' },
    { method: 'POST', endpoint: '/workflow/setting/voice', description: 'Voice Configuration', path: '/api-reference/voice/getconfig' },
  ];

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'POST': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'PUT': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'DELETE': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Environment Toggle */}
      <div className="mb-6">
        <EnvironmentToggle />
      </div>

      {/* Header */}
      <section>
        <h1 className="text-4xl font-bold text-[var(--text-primary)] mb-2">
          MenuBuilder API Overview
        </h1>
        <p className="text-[var(--text-secondary)] text-lg">
          The SnapMenu API allows restaurants, POS systems, and third-party platforms to programmatically manage menus, orders, payments, and merchant settings. It's fully testable in a sandbox environment before production use.
        </p>
      </section>

      {/* Description */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Integration Features
        </h2>
        <ul className="list-disc list-inside space-y-2 text-[var(--text-secondary)] ml-4">
          <li>Onboard restaurants</li>
          <li>Manage menus</li>
          <li>Simulate and process orders</li>
          <li>Configure merchant settings</li>
          <li>Set up webhooks</li>
          <li>Manage payments</li>
          <li>Enable AI voice ordering</li>
        </ul>
      </section>

      {/* Base URL */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Base URL
        </h2>
        <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <code className="text-[var(--accent-color)] font-mono text-sm break-all">
              {apiBaseUrl}
            </code>
            <span className={`text-xs px-3 py-1 rounded-full font-semibold ${environment === 'sandbox'
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              }`}>
              {environment === 'sandbox' ? 'Sandbox' : 'Production'}
            </span>
          </div>
        </div>
      </section>

      {/* Authentication */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Authentication
        </h2>
        <div className="space-y-3">
          <p className="text-[var(--text-secondary)]">
            All requests require two headers:
          </p>
          <ul className="list-disc list-inside space-y-1 text-[var(--text-secondary)] ml-4">
            <li><strong>api-key:</strong> Your API key</li>
            <li><strong>restaurant-id:</strong> Your restaurant ID</li>
          </ul>
          
          <p className="text-[var(--text-secondary)] mt-4 mb-2">
            <strong>Example Request:</strong>
          </p>
          <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
            <pre className="text-sm font-mono overflow-x-auto">
              <code className="text-[var(--text-primary)]">
                {`curl -X GET "${apiBaseUrl}/restaurant" \\
  -H "api-key: a28beb13-644a-4efe-b66a-27e2b3b752c1" \\
  -H "restaurant-id: 123e4567-e89b-12d3-a456-426614174000"`}
              </code>
            </pre>
          </div>

          {currentApiKey && (
            <div className="bg-[var(--accent-color)]/10 border border-[var(--accent-color)]/20 rounded-lg p-4 mt-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Your API Key</h3>
                  <code className="text-[var(--accent-color)] font-mono text-sm bg-[var(--bg-secondary)] px-3 py-2 rounded border border-[var(--border-color)] inline-block">
                    {currentApiKey}
                  </code>
                </div>
                <button
                  onClick={() => copyToClipboard(currentApiKey)}
                  className="px-4 py-2 bg-[var(--accent-color)] text-white rounded-lg hover:opacity-90 transition-colors text-sm flex items-center space-x-2"
                  title="Copy API Key"
                >
                  <span>Copy</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Endpoints Summary */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Endpoints Summary
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-[var(--border-color)]">
            <thead>
              <tr className="bg-[var(--bg-secondary)]">
                <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Method</th>
                <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Endpoint</th>
                <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Description</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((endpoint, index) => (
                <tr key={index} className="hover:bg-[var(--bg-secondary)]/50 transition-colors">
                  <td className="border border-[var(--border-color)] px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getMethodColor(endpoint.method)}`}>
                      {endpoint.method}
                    </span>
                  </td>
                  <td className="border border-[var(--border-color)] px-4 py-3">
                    <Link
                      to={endpoint.path}
                      className="font-mono text-sm text-[var(--accent-color)] hover:underline"
                    >
                      {endpoint.endpoint}
                    </Link>
                  </td>
                  <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">
                    {endpoint.description}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Request Example */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Request Example
        </h2>
        <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
          <pre className="text-sm font-mono overflow-x-auto">
            <code className="text-[var(--text-primary)]">
              {`curl -X GET "${apiBaseUrl}/restaurant" \\
  -H "api-key: YOUR_API_KEY" \\
  -H "restaurant-id: YOUR_RESTAURANT_ID"`}
            </code>
          </pre>
        </div>
      </section>

      {/* Response Example */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Response Example
        </h2>
        <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
          <pre className="text-sm font-mono overflow-x-auto">
            <code className="text-[var(--text-primary)]">
              {`[
  {
    "restaurantId": "a2959265-3cc2-4565-98ee-f983584a639f",
    "name": "Sample Restaurant",
    "phone": "1234567890",
    "address": "123 Main Street",
    "state": "California",
    "zipcode": "90210",
    "country": "United States",
    "owner": "John Doe",
    "alternativePhone": "1234567891",
    "cuisineType": "Italian"
  }
]`}
            </code>
          </pre>
        </div>
      </section>

      {/* Error Handling */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Error Handling
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-[var(--border-color)]">
            <thead>
              <tr className="bg-[var(--bg-secondary)]">
                <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Code</th>
                <th className="border border-[var(--border-color)] px-4 py-3 text-left font-semibold text-[var(--text-primary)]">Meaning</th>
              </tr>
            </thead>
            <tbody>
              <tr className="hover:bg-[var(--bg-secondary)]/50">
                <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-green-600 dark:text-green-400">200</td>
                <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Success</td>
              </tr>
              <tr className="hover:bg-[var(--bg-secondary)]/50">
                <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-amber-600 dark:text-amber-400">400</td>
                <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Bad Request</td>
              </tr>
              <tr className="hover:bg-[var(--bg-secondary)]/50">
                <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-red-600 dark:text-red-400">401</td>
                <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Unauthorized</td>
              </tr>
              <tr className="hover:bg-[var(--bg-secondary)]/50">
                <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-red-600 dark:text-red-400">404</td>
                <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Not Found</td>
              </tr>
              <tr className="hover:bg-[var(--bg-secondary)]/50">
                <td className="border border-[var(--border-color)] px-4 py-3 font-mono text-red-600 dark:text-red-400">500</td>
                <td className="border border-[var(--border-color)] px-4 py-3 text-[var(--text-secondary)]">Server Error</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Error Format */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Error Response Example
        </h2>
        <div className="bg-[var(--bg-secondary)] rounded-lg p-4 border border-[var(--border-color)]">
          <pre className="text-sm font-mono overflow-x-auto">
            <code className="text-[var(--text-primary)]">
              {`{
  "error": "Invalid authentication credentials",
  "status": false
}`}
            </code>
          </pre>
        </div>
      </section>

      {/* Support */}
      <section>
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
          Support
        </h2>
        <p className="text-[var(--text-secondary)]">
          For technical support or API key requests, please contact:
        </p>
        <p className="text-[var(--accent-color)] mt-2">
          <strong>Email:</strong> support@snapmenu.ai
        </p>
      </section>
    </div>
  );
};
